import { PRODUCT_NAME } from "./brand.js";

// After a deploy, a tab opened before it may ask for a page's code by its old
// hashed name (design v0.6, C1). Deploys keep old files for a week, so this is
// rare; when it happens the tab reloads once into the new version, and if that
// doesn't help it says so plainly, never with a raw error page. Shared by the
// staff app, the portal and the operator app.

const KEY = "staffsmith.staleDeployReload";
// A second failure this soon after a reload means reloading isn't helping.
const LOOP_WINDOW_MS = 60_000;

export const STALE_DEPLOY_MESSAGE = `A new version of ${PRODUCT_NAME} is available.`;

export type StaleDeployEnv = {
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  reload: () => void;
  now: () => number;
};

const browserEnv = (): StaleDeployEnv => ({
  storage: sessionStorage,
  reload: () => location.reload(),
  now: Date.now,
});

// What browsers say when a dynamic import or its preload can't be fetched.
export function isChunkLoadError(e: unknown): boolean {
  const message = e instanceof Error ? e.message : String(e);
  return /dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    message,
  );
}

// Reloads once ("reloading"), or reports that a reload has already been tried
// and didn't help ("stale"). The guard lives in sessionStorage, so it can't loop.
export function recoverFromStaleDeploy(env: StaleDeployEnv = browserEnv()): "reloading" | "stale" {
  try {
    const last = Number(env.storage.getItem(KEY) ?? 0);
    if (last && env.now() - last < LOOP_WINDOW_MS) return "stale";
    env.storage.setItem(KEY, String(env.now()));
  } catch {
    // No storage, no guard: don't risk a reload loop.
    return "stale";
  }
  env.reload();
  return "reloading";
}

// The banner's Reload: the person asked, so the guard is cleared first.
export function reloadIntoNewVersion(env: StaleDeployEnv = browserEnv()) {
  try {
    env.storage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
  env.reload();
}

// A bar across the top of the page, drawn without React so it shows even when
// the app's own code can't load. Shown once.
export function showStaleDeployBanner(doc: Document = document) {
  if (doc.getElementById("stale-deploy-banner")) return;
  const bar = doc.createElement("div");
  bar.id = "stale-deploy-banner";
  bar.setAttribute("role", "status");
  bar.style.cssText =
    "position:sticky;top:0;z-index:1000;display:flex;gap:1rem;align-items:center;" +
    "justify-content:center;padding:0.6rem 1rem;background:var(--notice-bg,#eef5fa);" +
    "color:var(--text,#1d1d1b);border-bottom:1px solid var(--line,#e2e2dc);font:inherit;";
  const text = doc.createElement("span");
  text.textContent = STALE_DEPLOY_MESSAGE;
  const button = doc.createElement("button");
  button.type = "button";
  button.textContent = "Reload";
  button.addEventListener("click", () => reloadIntoNewVersion());
  bar.append(text, button);
  doc.body.prepend(bar);
}

// For a route's lazy import: the page's module, or, after a stale deploy,
// nothing while the tab reloads (a promise that never settles, so the current
// page stays put) or null with the banner up.
export async function importPage<T>(
  load: () => Promise<T>,
  env?: StaleDeployEnv,
  banner: () => void = () => showStaleDeployBanner(),
): Promise<T | null> {
  try {
    return await load();
  } catch (e) {
    if (!isChunkLoadError(e)) throw e;
    if (recoverFromStaleDeploy(env) === "reloading") return new Promise<never>(() => {});
    banner();
    return null;
  }
}

// Vite reports a failed preload of a page's code or styles with this event.
export function watchForStaleDeploy(target: Window = window) {
  target.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    if (recoverFromStaleDeploy() === "stale") showStaleDeployBanner();
  });
}
